import React, { useState } from "react";

import { Stack, SubmissionForm, useTheme } from "@bps/fluent-ui";
import { ValidationSchema, Validator } from "@bps/validator";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import {
  BulkActionResponse,
  DeploymentRing
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useAssignTenantDeploymentRingBulkAction,
  useDeploymentRings
} from "@libs/api/gateways/field/field-ops-gateway.hooks";

import {
  AssignDeploymentRingFormValues,
  BulkActionType,
  BulkAssignTenantDeploymentRingFormValues
} from "../../formTypes";
import { BulkActionFormWrapper } from "../BulkActionFormWrapper";
import { DeploymentRingList } from "./DeploymentRingList";

const validator = new Validator<BulkAssignTenantDeploymentRingFormValues>();

const schema: ValidationSchema<BulkAssignTenantDeploymentRingFormValues> = {
  selectorId: validator.string().required({ message: "Selector is required" }),
  actionType: validator
    .string()
    .required({ message: "Action type is required" })
};

export const AssignTenantDeploymentRingForm: React.FC = () => {
  const theme = useTheme();

  const query = useDeploymentRings();

  const [responseMessage, setResponseMessage] = useState<
    BulkActionResponse | undefined
  >();

  const {
    mutateAsync: assignTenantDeploymentRingAction
  } = useAssignTenantDeploymentRingBulkAction();

  const onSubmit = async (
    formValues: BulkAssignTenantDeploymentRingFormValues
  ) => {
    const {
      selectorId,
      clientDeploymentRings,
      serverDeploymentRings
    } = formValues;
    if (!clientDeploymentRings || !serverDeploymentRings || !selectorId) return;

    // Submit request for bulk action

    const response = await assignTenantDeploymentRingAction({
      clientDeploymentRingIdList: mapSelectedDeploymentRingsToOrderedIdList(
        clientDeploymentRings
      ),
      serverDeploymentRingIdList: mapSelectedDeploymentRingsToOrderedIdList(
        serverDeploymentRings
      ),
      selectorId
    });

    setResponseMessage(response);
  };

  return (
    <SubmissionForm<BulkAssignTenantDeploymentRingFormValues>
      onSubmit={onSubmit}
      initialValues={{
        actionType: BulkActionType.AssignDeploymentRing,
        clientDeploymentRings: [],
        serverDeploymentRings: [],
        selectorId: undefined
      }}
      validate={values => validator.validate(values, schema)}
      hideButtons
    >
      {(state, actions) => {
        // Required to register fields since it does not exist at field control
        !state.fields["clientDeploymentRings"] &&
          actions.registerField("clientDeploymentRings");

        !state.fields["serverDeploymentRings"] &&
          actions.registerField("serverDeploymentRings");

        return (
          <BulkActionFormWrapper responseMessage={responseMessage}>
            <QueryStateIndicator<DeploymentRing[]>
              allowNullOrUndefined
              {...query}
            >
              {deploymentRings => (
                <Stack horizontal tokens={{ childrenGap: theme.spacing.l1 }}>
                  <DeploymentRingList
                    title="Select Server Deployment Ring(s) to Assign"
                    deploymentRings={deploymentRings}
                    keyName="serverDeploymentRings"
                  />
                  <DeploymentRingList
                    title="Select Client Deployment Ring(s) to Assign"
                    deploymentRings={deploymentRings}
                    keyName="clientDeploymentRings"
                  />
                </Stack>
              )}
            </QueryStateIndicator>
          </BulkActionFormWrapper>
        );
      }}
    </SubmissionForm>
  );
};

function mapSelectedDeploymentRingsToOrderedIdList(
  deploymentRings: AssignDeploymentRingFormValues[]
) {
  return deploymentRings
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(x => {
      return { deploymentRingId: x.id };
    });
}
